import React, { useState, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import axios from "axios";
import config from "../../../Helpers/config.json";
import { errorMessages } from "../../../Helpers/ErrorMessages";
import CustomDialog from "../../../Utils/Dialog/CustomDialog";
import { Button } from "../../../Utils/styledComponents";
import TextField from "../../../Utils/FormInputs/TextField";
import { CustomSelect } from "../../../Utils/FormInputs/SelectInput";
import { formatAmount, formatDigits } from "../../../Utils/formatAmount";

export const ExpenseModal = ({ open, onClose, editingExpense, actions, onSuccess }) => {
  const [state, setState] = useState({
    description: "",
    expense_date: "",
    vendor: null,
    category: null,
    payment: null,
  });

  const [rows, setRows] = useState([{ uuid_id: uuidv4(), head_name: { label: "", value: "" }, amount: "", memo: "" }]);
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  const [expenseAccounts, setExpenseAccounts] = useState([]);
  const [vendorList, setVendorList] = useState([]);
  const [grandTotal, setGrandTotal] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState(null);

  // -------------------- Notification Helper --------------------
  const showNotification = (message, type = "error") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000); // auto-hide
  };

  const notify = (message, type = "error") => {
    if (actions?.snackbarActions?.[`${type}Snackbar`]) {
      actions.snackbarActions[`${type}Snackbar`](message);
    } else {
      showNotification(message, type);
    }
  };

  // -------------------- Fetch Payment Accounts --------------------
  useEffect(() => {
    const fetchPaymentAccounts = async () => {
      try {
        const res = await axios.get(`${config.epharmUrl}/accounting/get_cash_and_bank_accounts`);
        const accounts = res.data ?? [];
        setPaymentAccounts(accounts.map((i) => ({ label: i.head_name, value: i.id })));
      } catch (err) {
        console.error("Error fetching payment accounts:", err);
        notify("Failed to fetch payment accounts", "error");
      }
    };
    fetchPaymentAccounts();
  }, [actions]);

  // -------------------- Fetch Expense Accounts --------------------
  useEffect(() => {
    const fetchExpenseAccounts = async () => {
      try {
        const res = await axios.get(`${config.epharmUrl}/accounting/get_expenditure_accounts`);
        const accounts = res.data ?? [];
        setExpenseAccounts(accounts.map((i) => ({ label: i.head_name, value: i.id })));
      } catch (err) {
        console.error("Error fetching expense accounts:", err);
        notify("Failed to fetch expense accounts", "error");
      }
    };
    fetchExpenseAccounts();
  }, [actions]);

  // -------------------- Fetch Vendors --------------------
  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const res = await axios.get(`${config.epharmUrl}/accounting/vendor_list`);
        const vendors = res.data?.data ?? [];
        setVendorList(vendors.map((v) => ({ label: v.Name, value: v.id })));
      } catch (err) {
        console.error("Error fetching vendors:", err);
        notify("Failed to fetch vendors", "error");
      }
    };
    fetchVendors();
  }, [actions]);

  // -------------------- Prefill Form --------------------
  useEffect(() => {
    if (!editingExpense) {
      setState({ description: "", expense_date: "", vendor: null, category: null, payment: null });
      setRows([{ uuid_id: uuidv4(), head_name: { label: "", value: "" }, amount: "", memo: "" }]);
      setGrandTotal(0);
      return;
    }

    setState({
      description: editingExpense.details || "",
      expense_date: editingExpense.expense_date || "",
      vendor: editingExpense.vendor
        ? { label: editingExpense.vendor_name, value: editingExpense.vendor_id }
        : null,
      category: editingExpense.category
        ? { label: editingExpense.category, value: editingExpense.category_id }
        : null,
      payment: paymentAccounts.find((a) => a.value === editingExpense.payment_account_id) || null,
    });

    const mappedRows = (editingExpense.expense_info || []).map((item) => ({
      uuid_id: item.uuid_id || uuidv4(),
      head_name:
        expenseAccounts.find((a) => a.value === item.expense_account_id) || { label: item.expense_account || "", value: item.expense_account_id },
      amount: item.amount || "",
      memo: item.memo || "",
    }));

    setRows(mappedRows);
    calculateGrandTotal(mappedRows);
  }, [editingExpense, expenseAccounts, paymentAccounts]);

  // -------------------- Field & Row Handlers --------------------
  const handleChangeField = (field, value) => setState(prev => ({ ...prev, [field]: value }));

  const handleAddRow = () => setRows(prev => [...prev, { uuid_id: uuidv4(), head_name: { label: "", value: "" }, amount: "", memo: "" }]);
  const handleRemoveRow = (uuid_id) => {
    const updated = rows.filter(r => r.uuid_id !== uuid_id);
    setRows(updated);
    calculateGrandTotal(updated);
  };
  const handleChangeRow = (uuid_id, field, value) => {
    const updated = rows.map(r => r.uuid_id === uuid_id ? { ...r, [field]: value } : r);
    setRows(updated);
    if (field === "amount") calculateGrandTotal(updated);
  };

  // -------------------- Calculate Total --------------------
  const calculateGrandTotal = (rowsData = rows) => {
    let total = 0, hasInvalid = false;
    rowsData.forEach(r => {
      const amt = Number(r.amount);
      if (isNaN(amt) || amt < 0) hasInvalid = true;
      total += isNaN(amt) ? 0 : amt;
    });
    setGrandTotal(total);
    if (hasInvalid) notify("One or more amounts are invalid!", "error");
    return total;
  };

  // -------------------- Submit --------------------
  const handleSubmit = async () => {
    const payload = {
      expense_date: state.expense_date,
      total_amount: calculateGrandTotal(),
      bank_account: state.payment?.value,
      expense_details: state.description,
      category: state.category?.value,
      vendor: state.vendor?.value,
      expense_info: rows.map(r => ({
        expense_account: r.head_name?.value,
        amount: r.amount,
        memo: r.memo,
      })),
    };

    setIsSubmitting(true);
    notify("Saving expense...", "info");

    try {
      if (editingExpense?.id) {
        await axios.put(`${config.epharmUrl}/accounting/update_expense/${editingExpense.id}`, payload);
        notify("Expense updated successfully", "success");
        if (onSuccess) onSuccess("Expense updated successfully");
      } else {
        await axios.post(`${config.epharmUrl}/accounting/insert_expenses`, payload);
        notify("Expense recorded successfully", "success");
        if (onSuccess) onSuccess("Expense recorded successfully");
      }
    } catch (err) {
      console.error("Error saving expense:", err);
      notify("Failed to save expense", "error");
      errorMessages(err, null, actions);
    } finally {
      setIsSubmitting(false);
    }
  };

  // -------------------- Render --------------------
// -------------------- Render --------------------
return (
  <CustomDialog open={open} handleClose={onClose} title={editingExpense ? "Edit Expense" : "Record Expense"} maxWidth="xl">
    <div style={{
      display: "flex",
      flexDirection: "column",
      height: "80vh",
      gap: "1rem",
      fontFamily: "Segoe UI, Arial, sans-serif",
      backgroundColor: "#f9fafb", // soft off-white
      padding: "1rem"
    }}>

      {/* Notification */}
      {notification && (
        <div style={{
          padding: "0.5rem 1rem",
          borderRadius: "6px",
          backgroundColor: notification.type === "error" ? "#ff6b6b" : notification.type === "success" ? "#4ade80" : "#60a5fa",
          color: "#fff",
          fontWeight: "600",
          textAlign: "center",
          boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
        }}>
          {notification.message}
        </div>
      )}

      {/* Details */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "1rem" }}>
        <TextField
          label="Date"
          type="date"
          value={state.expense_date}
          onChange={e => handleChangeField("expense_date", e.target.value)}
          required
        />
        <CustomSelect
          label="Payment Method"
          value={state.payment}
          onChange={value => handleChangeField("payment", value)}
          options={paymentAccounts}
          placeholder="Select payment method..."
          required
        />
        <CustomSelect
          label="Pay to the order of"
          value={state.vendor}
          onChange={value => handleChangeField("vendor", value)}
          options={vendorList}
          placeholder="Select vendor..."
          required
        />
        <TextField
          label="Description"
          value={state.description}
          onChange={e => handleChangeField("description", e.target.value)}
          fullWidth
        />
      </div>

      {/* Rows */}
      <div style={{
        flexGrow: 1,
        display: "flex",
        flexDirection: "column",
        border: "1px solid #e5e7eb",
        borderRadius: "8px",
        background: "#ffffff",
        padding: "1rem",
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)"
      }}>
        <h4 style={{ marginBottom: "0.5rem", color: "#1f2937" }}>Expense Accounts</h4>
        <div style={{
          display: "grid",
          gridTemplateColumns: "2fr 1fr 2fr 0.5fr",
          gap: "0.5rem",
          fontWeight: "600",
          borderBottom: "1px solid #d1d5db",
          paddingBottom: "0.5rem",
          color: "#4b5563"
        }}>
          <span>Account</span>
          <span>Amount</span>
          <span>Memo</span>
          <span>Action</span>
        </div>
        <div style={{ overflowY: "auto", flexGrow: 1, paddingTop: "0.5rem" }}>
          {rows.map(row => (
            <div key={row.uuid_id} style={{ display: "grid", gridTemplateColumns: "2fr 1fr 2fr 0.5fr", gap: "0.5rem", alignItems: "center", marginBottom: "0.5rem" }}>
              <CustomSelect value={row.head_name || { label: "", value: "" }} onChange={value => handleChangeRow(row.uuid_id, "head_name", value)} options={expenseAccounts} placeholder="Select account" />
              <TextField type="number" value={row.amount} onChange={e => handleChangeRow(row.uuid_id, "amount", e.target.value)} />
              <TextField type="text" value={row.memo} onChange={e => handleChangeRow(row.uuid_id, "memo", e.target.value)} />
              <Button style={{
                backgroundColor: "#ef4444", // modern red
                color: "#fff",
                borderRadius: "6px",
                padding: "0.25rem 0.5rem",
                fontWeight: "bold",
                transition: "background 0.2s",
              }} onClick={() => handleRemoveRow(row.uuid_id)}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = "#dc2626"}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = "#ef4444"}
              >✕</Button>
            </div>
          ))}
        </div>
        <Button style={{
          marginTop: "0.5rem",
          backgroundColor: "#3b82f6",
          color: "#fff",
          borderRadius: "6px",
          padding: "0.5rem 1rem",
          fontWeight: "600",
          alignSelf: "flex-start",
          transition: "background 0.2s"
        }}
          onMouseEnter={e => e.currentTarget.style.backgroundColor = "#2563eb"}
          onMouseLeave={e => e.currentTarget.style.backgroundColor = "#3b82f6"}
          onClick={handleAddRow}
        >
          + Add Row
        </Button>
      </div>

      {/* Sticky Footer */}
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        position: "sticky",
        bottom: 0,
        background: "#f3f4f6",
        padding: "0.75rem 1rem",
        borderTop: "1px solid #d1d5db",
      }}>
        <div style={{ fontWeight: "600", fontSize: "1.2rem", color: "#111827" }}>Grand Total: {formatAmount(grandTotal)}</div>
        <Button style={{
          padding: "0.75rem 1.5rem",
          backgroundColor: "#10b981", // teal green
          color: "#fff",
          fontWeight: "600",
          borderRadius: "6px",
          transition: "background 0.2s"
        }}
          onMouseEnter={e => e.currentTarget.style.backgroundColor = "#059669"}
          onMouseLeave={e => e.currentTarget.style.backgroundColor = "#10b981"}
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? "Saving..." : editingExpense ? "Update Expense" : "Save Expense"}
        </Button>
      </div>
    </div>
  </CustomDialog>
);

};
